'use client';

import { Access, ModelAccessMappings } from '@/core/auth/access';
import { useSession } from '@/core/auth/use-session';
import { useAdmin } from '@/core/auth/use-admin';
import { Slot } from '@radix-ui/react-slot';
import { SpikemarkResource } from '@spikemark/auth';
import { FormControl, useFormField } from '@spikemark/ui/components/ui/form';
import { formatDate } from '@spikemark/ui/utils/format';
import * as React from 'react';
import { FC, PropsWithChildren, createContext, useCallback, useContext, useMemo } from 'react';
import { useFormState } from 'react-hook-form';
type FormAccessContextType = {
  hasWriteAccessToField: (fieldName: string) => boolean;
  hasNoAccess: boolean;
  hasFullAccess: boolean;
};
const FormAccessContext = createContext<FormAccessContextType>({} as FormAccessContextType);
export const FormAccessProvider: FC<PropsWithChildren<{
  resource: SpikemarkResource;
  action: 'create' | 'update';
  data: ModelAccessMappings[SpikemarkResource];
}>> = ({
  children,
  resource,
  action,
  data
}) => {
  const {
    data: session
  } = useSession();
  const isAdmin = useAdmin();
  const access = useMemo(() => {
    if (isAdmin) {
      return {
        granted: true
      };
    }
    return Access.can(session?.user, resource, action, undefined, data);
  }, [session?.user, resource, action, data, isAdmin]);
  const hasWriteAccessToField = useCallback((fieldName: string) => {
    if (isAdmin) {
      return true;
    }
    return access.granted && (!access.restrict || access.restrict.includes(fieldName));
  }, [access.granted, access.restrict, isAdmin]);
  const hasNoAccess = !access.granted;
  const hasFullAccess = access.granted && (!access.restrict || access.restrict.length === 0);
  const value = useMemo(() => ({
    hasWriteAccessToField,
    hasNoAccess,
    hasFullAccess
  }), [hasNoAccess, hasWriteAccessToField, hasFullAccess]);
  return <FormAccessContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="FormAccessProvider" data-sentry-source-file="form-access.tsx">{children}</FormAccessContext.Provider>;
};
export const useFormAccessContext = () => useContext(FormAccessContext);
export const FormAccessControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot> & {
  readOnlyFallback?: React.ReactNode;
}>(({
  readOnlyFallback,
  ...props
}, ref) => {
  const {
    hasWriteAccessToField
  } = useFormAccessContext();
  const {
    defaultValues
  } = useFormState();
  const {
    name
  } = useFormField();
  return <FormControl ref={ref} {...props}>
      {hasWriteAccessToField(name) ? props.children : <div className="min-h-[24px] text-sm text-secondary select-none cursor-not-allowed">
          {readOnlyFallback ?? renderReadOnlyLabel(defaultValues?.[name])}
        </div>}
    </FormControl>;
});
function renderReadOnlyLabel(value: unknown): React.ReactNode {
  if (value instanceof Date) {
    return formatDate(value);
  } else if (typeof value === 'string' || typeof value === 'number') {
    return value;
  } else if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return '-';
}